/**
 * Atividade dos usuários no portal — espelha o contexto `audit` do back.
 *
 * Os rótulos das ações ("Curtiu a aula") e das categorias vêm prontos da API
 * (`/audit/activity` e `/audit/actions`); aqui fica só o que é de tela:
 * agrupar por dia, tempo relativo, dispositivo e o detalhe de cada ação.
 */
import type { components } from '@/core/http';

export type Activity = components['schemas']['ActivityOut'];
export type ActivityPage = components['schemas']['ActivityPageOut'];
export type ActivitySummary = components['schemas']['ActivitySummaryOut'];
export type ActionCatalog = components['schemas']['CatalogOut'];
export type Category = components['schemas']['Category'];
export type Device = components['schemas']['DeviceOut'];

/** Ordem das categorias nos filtros e no resumo. */
export const CATEGORIES: Category[] = [
  'session',
  'navigation',
  'learning',
  'events',
  'products',
  'assistant',
  'visits',
];

export function isCategory(value: unknown): value is Category {
  return typeof value === 'string' && (CATEGORIES as string[]).includes(value);
}

// ──────────────────────────── Período ────────────────────────────

export type Period = 'hoje' | '7d' | '30d' | 'tudo';

export const PERIODS: { value: Period; label: string }[] = [
  { value: 'hoje', label: 'Hoje' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: 'tudo', label: 'Desde o início' },
];

export function isPeriod(value: unknown): value is Period {
  return PERIODS.some((p) => p.value === value);
}

/** Início do período (ISO), no fuso de quem olha; `undefined` para "desde o início". */
export function periodStart(period: Period, now = new Date()): string | undefined {
  if (period === 'tudo') return undefined;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (period === '7d') start.setDate(start.getDate() - 6);
  if (period === '30d') start.setDate(start.getDate() - 29);
  return start.toISOString();
}

// ──────────────────────────── Linha do tempo ────────────────────────────

export interface DayGroup {
  /** `AAAA-MM-DD` no fuso local — chave estável para o React. */
  key: string;
  label: string;
  items: Activity[];
}

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
const fullDate = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });

/** "Hoje", "Ontem", "segunda-feira, 13 de outubro" (com o ano, se for de outro ano). */
export function dayLabel(date: Date, now = new Date()): string {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(date) === dayKey(now)) return 'Hoje';
  if (dayKey(date) === dayKey(yesterday)) return 'Ontem';
  const label = date.getFullYear() === now.getFullYear() ? weekday.format(date) : fullDate.format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Agrupa por dia, na ordem em que chegam (a API manda do mais novo para o mais antigo). */
export function groupByDay(items: readonly Activity[], now = new Date()): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const item of items) {
    const date = new Date(item.occurred_at);
    const key = dayKey(date);
    const last = groups.at(-1);
    if (last?.key === key) last.items.push(item);
    else groups.push({ key, label: dayLabel(date, now), items: [item] });
  }
  return groups;
}

const clock = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** "agora", "há 5 min", "há 3 h" nas últimas horas; depois, o horário (o dia está no grupo). */
export function relativeTime(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  if (minutes < 6 * 60) return `há ${Math.floor(minutes / 60)} h`;
  return clock.format(date);
}

const exact = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full', timeStyle: 'medium' });

/** Data e hora completas — o `title` do tempo relativo. */
export function exactTime(iso: string): string {
  return exact.format(new Date(iso));
}

// ──────────────────────────── Dispositivo ────────────────────────────

export const DEVICE_LABELS: Record<string, string> = {
  desktop: 'Computador',
  mobile: 'Celular',
  tablet: 'Tablet',
  tv: 'TV',
};

/** "Celular · Safari · iOS". O que o navegador declara; "Other" não diz nada e fica de fora. */
export function deviceLabel(device: Device | null | undefined): string | undefined {
  if (!device) return undefined;
  const parts = [DEVICE_LABELS[device.kind] ?? device.kind, device.browser, device.os].filter(
    (p) => p && p !== 'Other',
  );
  return parts.join(' · ');
}

// ──────────────────────────── Detalhe da ação ────────────────────────────

/** Como um fluxo guiado da Centuria terminou (`flow.completed`). */
const FLOW_OUTCOMES: Record<string, string> = {
  completed: 'Chegou ao fim',
  handoff: 'Seguiu para a conversa com a IA',
  failed: 'Encerrado por um erro no fluxo',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * O detalhe que acompanha o rótulo, a partir dos metadados — nunca há texto
 * da pessoa neles (a API só guarda tamanhos, notas e marcadores).
 */
export function activityDetail(item: Pick<Activity, 'action' | 'metadata'>): string | undefined {
  const m = item.metadata;
  switch (item.action) {
    case 'lesson.rated':
      return typeof m.score === 'number'
        ? `Nota ${m.score}${m.with_comment === true ? ', com comentário' : ''}`
        : undefined;
    case 'search.performed':
      return typeof m.chars === 'number'
        ? `Termo de ${plural(m.chars, 'caractere', 'caracteres')}`
        : undefined;
    case 'assistant.asked':
      if (typeof m.question_chars !== 'number') return undefined;
      return `${m.new_conversation === true ? 'Nova conversa · ' : ''}pergunta de ${plural(
        m.question_chars,
        'caractere',
        'caracteres',
      )}`;
    case 'lesson.played':
      return typeof m.position_s === 'number' && m.position_s > 0
        ? `A partir de ${formatPosition(m.position_s)}`
        : undefined;
    case 'session.login':
      return m.method === 'password' ? 'Com usuário e senha' : undefined;
    case 'flow.started':
      return typeof m.version === 'number' ? `Versão ${m.version}` : undefined;
    case 'flow.completed': {
      // Só o desfecho e a versão — as respostas ficam nos resultados do fluxo.
      const outcome = typeof m.outcome === 'string' ? FLOW_OUTCOMES[m.outcome] : undefined;
      const version = typeof m.version === 'number' ? `versão ${m.version}` : undefined;
      return [outcome, version].filter(Boolean).join(' · ') || undefined;
    }
    default:
      return undefined;
  }
}

function formatPosition(seconds: number): string {
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(h ? 2 : 1, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** O alvo em texto: o nome fotografado, ou a página visitada. */
export function targetText(item: Pick<Activity, 'target_label' | 'path' | 'action'>): string | undefined {
  if (item.target_label) return item.target_label;
  return item.action === 'page.viewed' ? (item.path ?? undefined) : undefined;
}

// ──────────────────────────── Resumo da pessoa ────────────────────────────

/** Categorias com ação nos 30 dias, da maior para a menor, com a fatia de cada uma (%). */
export function categoryShares(
  summary: Pick<ActivitySummary, 'total' | 'by_category'>,
): { category: string; label: string; count: number; percent: number }[] {
  if (summary.total === 0) return [];
  return summary.by_category
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count)
    .map((c) => ({ ...c, percent: Math.round((c.count / summary.total) * 100) }));
}

/** "Nunca", "há 5 min", "Ontem às 14:03", "13 de out. às 09:10". */
export function lastSeenText(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return 'Nunca';
  const date = new Date(iso);
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 6 * 60) return relativeTime(iso, now);
  const day = dayLabel(date, now);
  const when = day === 'Hoje' || day === 'Ontem' ? day : shortDate.format(date);
  return `${when} às ${clock.format(date)}`;
}

const shortDate = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short' });
